import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Bot, Loader2, Pause, Play, RefreshCw, Send, Sparkles, Volume2, VolumeX, X,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { useVoice } from "@/hooks/useVoice";
import { PM_ROBOT, PM_ROBOT_AVATAR, SEMAFORO } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const Blocco = ({ titolo, voci, colore, testid }) =>
  !voci?.length ? null : (
    <div data-testid={testid}>
      <p className={`font-mono text-[10px] font-bold uppercase tracking-[0.16em] ${colore}`}>{titolo}</p>
      <ul className="mt-2 space-y-1.5">
        {voci.map((v, i) => (
          <li key={i} className="flex gap-2 text-[13px] leading-relaxed text-slate-700">
            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-slate-300" />
            <span>{v}</span>
          </li>
        ))}
      </ul>
    </div>
  );

export const PmBot = ({ pagina = "dashboard", sprint = null }) => {
  const { projectId } = useProject();
  const [aperto, setAperto] = useState(false);
  const [loading, setLoading] = useState(false);
  const [analisi, setAnalisi] = useState(null);
  const [domanda, setDomanda] = useState("");
  const [chat, setChat] = useState([]);
  const [chatBusy, setChatBusy] = useState(false);
  const voce = useVoice();
  const fondo = useRef(null);

  const daLeggere = (a) =>
    [a.sintesi, a.criticita?.length ? `Il punto critico: ${a.criticita[0]}` : "",
     a.prossime_azioni?.length ? `La prima cosa da fare: ${a.prossime_azioni[0]}` : ""]
      .filter(Boolean).join(" ").slice(0, 700);

  const analizza = async () => {
    if (!projectId) return;
    setLoading(true);
    voce.stop();
    try {
      const { data } = await api.post(`/projects/${projectId}/pm-review`, { pagina, sprint });
      setAnalisi(data);
      voce.parla(daLeggere(data));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const chiedi = async (e) => {
    e.preventDefault();
    const q = domanda.trim();
    if (!q || chatBusy || !projectId) return;
    setDomanda("");
    setChat((c) => [...c, { domanda: q, risposta: null }]);
    setChatBusy(true);
    voce.stop();
    try {
      const { data } = await api.post(`/projects/${projectId}/pm-chat`, { domanda: q, pagina, sprint });
      setChat((c) => c.map((m, i) => (i === c.length - 1 ? { ...m, risposta: data.risposta } : m)));
      voce.parla(data.risposta);
    } catch (err) {
      toast.error(formatApiError(err));
      setChat((c) => c.slice(0, -1));
    } finally {
      setChatBusy(false);
    }
  };

  const apri = () => {
    setAperto(true);
    if (!analisi && !loading) analizza();
  };

  const chiudi = () => {
    setAperto(false);
    voce.stop();
  };

  useEffect(() => {
    fondo.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat, analisi]);

  const sem = SEMAFORO[analisi?.semaforo] || SEMAFORO.giallo;
  const sottotitolo = voce.battute[voce.indice];

  return (
    <>
      {!aperto && (
        <button
          onClick={apri}
          data-testid="pmbot-open-button"
          className="group fixed bottom-5 right-5 z-[9999] flex items-center gap-3 rounded-full border border-slate-200 bg-white/95 py-2 pl-2 pr-4 shadow-xl backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:shadow-2xl sm:bottom-7 sm:right-7"
        >
          <span className="relative">
            <img src={PM_ROBOT_AVATAR} alt="PM-Bot" className="h-11 w-11 rounded-full object-cover" />
            <span className="absolute -right-0.5 -top-0.5 h-3 w-3 animate-pulse rounded-full border-2 border-white bg-emerald-500" />
          </span>
          <span className="text-left">
            <span className="block font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-amber-600">PM-Bot</span>
            <span className="block text-xs font-semibold text-slate-900">Come va lo sprint?</span>
          </span>
        </button>
      )}

      {aperto && (
        <div className="fixed inset-0 z-[9998] flex items-end justify-end bg-slate-900/25 p-0 backdrop-blur-sm sm:p-5">
          <div
            data-testid="pmbot-panel"
            className="flex h-[92vh] w-full flex-col overflow-hidden rounded-t-3xl border border-slate-200 bg-white shadow-2xl sm:h-[85vh] sm:max-w-[440px] sm:rounded-3xl"
          >
            <div className="flex items-center gap-3 border-b border-slate-800 bg-slate-900 px-4 py-3">
              <img src={PM_ROBOT} alt="PM-Bot" className="h-12 w-12 shrink-0 rounded-xl bg-white/10 object-cover" />
              <div className="min-w-0 flex-1">
                <p className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-amber-400">
                  PM-Bot · Project Manager
                </p>
                <p className="truncate text-sm font-semibold text-white">
                  {sprint ? `Review ${sprint}` : "Andamento del progetto"}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button onClick={voce.toggleMuto} data-testid="pmbot-mute-button" title={voce.muto ? "Riattiva voce" : "Silenzia"}
                        className="rounded-lg p-2 text-slate-300 transition-colors hover:bg-white/10 hover:text-white">
                  {voce.muto ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                </button>
                <button onClick={voce.toggleRiproduzione} data-testid="pmbot-play-button" title="Play / Pausa"
                        className="rounded-lg p-2 text-slate-300 transition-colors hover:bg-white/10 hover:text-white">
                  {voce.inRiproduzione ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </button>
                <button onClick={chiudi} data-testid="pmbot-close-button"
                        className="rounded-lg p-2 text-slate-300 transition-colors hover:bg-white/10 hover:text-white">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {(sottotitolo || voce.caricamento) && (
              <div className="border-b border-amber-100 bg-amber-50/80 px-4 py-2.5" data-testid="pmbot-subtitles">
                <p className="flex items-start gap-2 text-[13px] font-medium leading-snug text-slate-800">
                  {voce.caricamento ? (
                    <>
                      <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin text-amber-600" />
                      <span className="text-slate-500">Sto prendendo la parola…</span>
                    </>
                  ) : (
                    <>
                      <Volume2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                      <span>{sottotitolo}</span>
                    </>
                  )}
                </p>
              </div>
            )}

            <div className="flex-1 space-y-5 overflow-y-auto px-4 py-4">
              {loading && !analisi && (
                <div className="flex h-40 flex-col items-center justify-center gap-3" data-testid="pmbot-loading">
                  <Loader2 className="h-7 w-7 animate-spin text-slate-300" />
                  <p className="text-sm text-slate-400">Sto leggendo i dati dello sprint…</p>
                </div>
              )}

              {analisi && (
                <div className="space-y-4" data-testid="pmbot-analysis">
                  <div className={`rounded-xl border p-3.5 ${sem.box}`} data-testid="pmbot-semaforo">
                    <p className={`font-mono text-[10px] font-bold uppercase tracking-[0.16em] ${sem.text}`}>
                      {sem.label}
                    </p>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-800" data-testid="pmbot-sintesi">
                      {analisi.sintesi}
                    </p>
                  </div>
                  <Blocco titolo="Cosa sta andando bene" voci={analisi.va_bene} colore="text-emerald-600" testid="pmbot-va-bene" />
                  <Blocco titolo="Cosa è da migliorare" voci={analisi.da_migliorare} colore="text-amber-600" testid="pmbot-da-migliorare" />
                  <Blocco titolo="Cosa non sta andando" voci={analisi.criticita} colore="text-rose-600" testid="pmbot-criticita" />
                  <Blocco titolo="Prossime azioni" voci={analisi.prossime_azioni} colore="text-slate-500" testid="pmbot-azioni" />
                </div>
              )}

              {chat.map((m, i) => (
                <div key={i} className="space-y-2">
                  <p className="ml-auto max-w-[85%] rounded-2xl rounded-br-sm bg-slate-900 px-3.5 py-2 text-[13px] text-white"
                     data-testid={`pmbot-chat-question-${i}`}>
                    {m.domanda}
                  </p>
                  {m.risposta ? (
                    <div className="flex max-w-[92%] gap-2.5" data-testid={`pmbot-chat-answer-${i}`}>
                      <img src={PM_ROBOT_AVATAR} alt="" className="mt-0.5 h-7 w-7 shrink-0 rounded-full object-cover" />
                      <p className="rounded-2xl rounded-bl-sm bg-slate-100 px-3.5 py-2 text-[13px] leading-relaxed text-slate-800">
                        {m.risposta}
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-slate-400">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-xs">PM-Bot sta ragionando…</span>
                    </div>
                  )}
                </div>
              ))}
              <div ref={fondo} />
            </div>

            <form onSubmit={chiedi} className="border-t border-slate-200 bg-slate-50 px-3 py-3">
              {voce.disponibile && voce.voci.length > 0 && (
                <div className="mb-2.5 flex items-center gap-2">
                  <span className="font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400">Voce</span>
                  <div className="flex flex-wrap gap-1.5">
                    {voce.voci.map((v) => (
                      <button
                        key={v.voice_id}
                        type="button"
                        title={v.descrizione}
                        onClick={() => voce.scegliVoce(v.voice_id)}
                        data-testid={`pmbot-voice-${v.nome}`}
                        className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                          voce.voceId === v.voice_id
                            ? "border-slate-900 bg-slate-900 text-white"
                            : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"
                        }`}
                      >
                        {v.nome}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Input
                  value={domanda}
                  onChange={(e) => setDomanda(e.target.value)}
                  placeholder="Chiedi qualsiasi cosa sul progetto…"
                  className="h-10 flex-1 border-slate-200 bg-white text-sm"
                  data-testid="pmbot-chat-input"
                />
                <Button type="submit" disabled={chatBusy || !domanda.trim()}
                        className="h-10 w-10 shrink-0 bg-slate-900 p-0 text-white transition-colors hover:bg-slate-800"
                        data-testid="pmbot-chat-send">
                  {chatBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
                <Button type="button" variant="outline" onClick={analizza} disabled={loading}
                        className="h-10 w-10 shrink-0 border-slate-200 bg-white p-0"
                        title="Rigenera la review" data-testid="pmbot-refresh-button">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                </Button>
              </div>
              {!voce.disponibile && (
                <p className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-400" data-testid="pmbot-voice-off">
                  <Sparkles className="h-3 w-3" /> Voce non disponibile: leggi i sottotitoli
                </p>
              )}
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default PmBot;

export const PmBotIcon = Bot;
